import { includes, map, reduce, reverse, size } from "lodash";
import { getFacetName } from "@linkfluence/i18n";
import { Facet } from "@linkfluence/models/facet";
import { Locale } from "@linkfluence/models/locale";
import { Metric } from "@linkfluence/models/metric";
import { natural } from "@tootools/sort";
import { getAffectLabel, getFormatedCloud } from "@radarly/models/cloud";
import { FullFacet, getLabelFromReviewValue } from "@radarly/models/facets";
import {
  CloudTerm,
  CloudTerms,
  SearchFragmentTerm,
} from "@radarly/models/search";

export function getTermSeries(
  values: SearchFragmentTerm["data"]["aggs"]["platform"]["values"],
  facet: FullFacet,
  locale: Locale
) {
  if (!facet) {
    return [];
  }
  if (includes(CloudTerms, facet.id)) {
    return getFormatedCloud(values, facet.id as CloudTerm);
  } else {
    if (size(facet.values) > 0) {
      return map(facet.values, ({ id, label, color }) => {
        return {
          id: "" + id,
          label: label || getFacetName(locale, facet.id as Facet, id),
          color,
        };
      });
    } else {
      return map(values, (item) => {
        return {
          id: item.key,
          label: getLabelFromReviewValue(item, facet.id as Facet),
          color: null,
        };
      });
    }
  }
}

export function computeData(
  values: SearchFragmentTerm["data"]["aggs"]["platform"]["values"],
  facet: FullFacet,
  metric: Metric,
  mean?: number
): Record<string, number> {
  if (!facet) {
    return {};
  }
  return reduce(
    values,
    (acc, { key, counts }) => {
      const _key = facet.id === Facet.Focuses ? parseInt(key.substr(6)) : key;
      const count = counts[metric] || 0;
      acc[_key] = mean > 0 ? count / mean : count;
      return acc;
    },
    {}
  );
}

function getValueKey(value: string, facet: Facet): string {
  const mentionFacet = [
    Facet.Mentions,
    Facet.SignificantMentions,
    Facet.EmergingMentions,
  ];
  const hashtagFacet = [
    Facet.Hashtags,
    Facet.SignificantHashtags,
    Facet.EmergingHashtags,
  ];
  if (mentionFacet.includes(facet)) {
    return value.startsWith("@") ? value : `@${value}`;
  }
  if (hashtagFacet.includes(facet)) {
    return value.startsWith("#") ? value : `#${value}`;
  }
  if (facet === Facet.Affects) {
    return getAffectLabel(value);
  }
  return value;
}

export function getSeries(
  data: SearchFragmentTerm["data"],
  facet: FullFacet,
  metric: Metric,
  locale: Locale,
  hiddenTerms: string[] = [],
  isHideable: boolean = false,
  isEdition: boolean = false,
  limit?: number
) {
  const values =
    data && data.aggs && facet && data.aggs[facet.id]
      ? data.aggs[facet.id].values
      : [];
  const cleanValues =
    isEdition || !isHideable
      ? values
      : values.filter(
          (value) =>
            !hiddenTerms.includes(getValueKey(value.key, facet.id as Facet))
        );

  const computedData = computeData(cleanValues, facet, metric);
  const topValues = [
    ...reverse(natural(cleanValues, "counts." + metric)),
  ].splice(0, limit || cleanValues.length);
  const termSeries = getTermSeries(topValues, facet, locale);

  return map(termSeries, ({ id, label, color }) => {
    const count = computedData ? computedData[id] || 0 : 0;
    return {
      id,
      label,
      weight: count, // weight is necessary for cloud card
      count,
      color,
      isHidden: !!(
        isEdition &&
        isHideable &&
        hiddenTerms.includes(label ?? id)
      ),
    };
  });
}
